/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/broker_submissions-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-broker_submissions-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/broker_submissions-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, submitted_by_user_profile_id, submission_status_id, reviewed_by_user_profile_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, submitted_at, created_at, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/broker_submissions-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-broker_submissions-api-client.ts (API client)
 * - client/api/generated_schemas/broker_submissions.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/broker_submissions-entity-config.ts
 * Public fields: 10/10 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BrokerSubmissionsClient, BrokerSubmissionsQueryOptions } from '../generated_clients/generated-broker_submissions-api-client.js';
import {
  BrokerSubmissionsInsert,
  BrokerSubmissionsUpdate,
  BrokerSubmissionsFieldConfig
} from '../../types/generated_schemas/generated-broker_submissions-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/broker_submissions_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const brokerSubmissionsKeys = {
  all: ['broker_submissions'] as const,
  lists: () => [...brokerSubmissionsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<BrokerSubmissionsQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...brokerSubmissionsKeys.lists(), { filters, ...page }] as const,
  details: () => [...brokerSubmissionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...brokerSubmissionsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...brokerSubmissionsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...brokerSubmissionsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...brokerSubmissionsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...brokerSubmissionsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useBrokerSubmissionsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new BrokerSubmissionsClient();
};

/**
 * Hook to get all broker_submissions with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with broker_submissions data
 */
export const useBrokerSubmissionsList = (options?: BrokerSubmissionsQueryOptions) => {
  const client = useBrokerSubmissionsClient();
  
  return useQuery({
    queryKey: brokerSubmissionsKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single broker_submission by ID
 *
 * @param id - The broker_submission ID
 * @returns React Query result with single broker_submission data
 */
export const useBrokerSubmissions = (id: string) => {
  const client = useBrokerSubmissionsClient();
  
  return useQuery({
    queryKey: brokerSubmissionsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/broker_submissions
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/broker_submissions_business_hooks/
 */

/**
 * Hook to create a new broker_submission
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, submitted_at, created_at, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating broker_submission
 */
export const useCreateBrokerSubmissions = () => {
  const client = useBrokerSubmissionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: BrokerSubmissionsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brokerSubmissionsKeys.all });
    },
  });
};

/**
 * Hook to update an existing broker_submission
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating broker_submission
 */
export const useUpdateBrokerSubmissions = () => {
  const client = useBrokerSubmissionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BrokerSubmissionsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: brokerSubmissionsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: brokerSubmissionsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a broker_submission
 *
 * @returns React Query mutation for deleting broker_submission
 */
export const useDeleteBrokerSubmissions = () => {
  const client = useBrokerSubmissionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brokerSubmissionsKeys.all });
    },
  });
};

/**
 * Hook to count broker_submissions with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, submitted_by_user_profile_id, submission_status_id, reviewed_by_user_profile_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useBrokerSubmissionsCount = (filter?: Partial<Record<typeof BrokerSubmissionsFieldConfig.filterable[number], any>>) => {
  const client = useBrokerSubmissionsClient();
  
  return useQuery({
    queryKey: brokerSubmissionsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useBrokerSubmissionsFieldConfig = () => {
  return BrokerSubmissionsFieldConfig;
};